import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { AdminLayout } from '../../layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Settings,
  Trash2,
  AlertTriangle,
  Loader2,
  Database,
  ShieldAlert,
  Download,
  Upload,
  Shield,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export function SystemSettings() {
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [clearing, setClearing] = useState(false);
  const [clearResult, setClearResult] = useState(null);
  const [settings, setSettings] = useState({ hide_ew: false, hide_place: false, protected_username: '' });
  const [exportMode, setExportMode] = useState('all');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/system/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const updateSetting = async (key, value) => {
    try {
      await axios.put(`${API}/system/settings`, { [key]: value });
      setSettings(prev => ({ ...prev, [key]: value }));
      toast.success('Setting updated');
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  const handleClearData = async () => {
    if (confirmText !== 'DELETE ALL') {
      toast.error('Please type DELETE ALL to confirm');
      return;
    }

    setClearing(true);
    try {
      const response = await axios.delete(`${API}/system/clear-data`);
      setClearResult(response.data);
      toast.success('All data cleared successfully');
      setConfirmText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear data');
    } finally {
      setClearing(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/system/export?mode=${exportMode}`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bookmaker-export-${exportMode}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const response = await axios.post(`${API}/system/import`, data);
      toast.success(`Imported: ${JSON.stringify(response.data.imported)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import data');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="system-settings">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-100">System Settings</h1>
          <p className="text-sm text-zinc-500">Manage system data, configuration and trading options</p>
        </div>

        {/* Trading Options */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="py-4">
            <CardTitle className="text-base font-heading text-zinc-100 flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-500" />
              Trading Options
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Hide E/W Toggle */}
            <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded border border-zinc-800">
              <div>
                <h3 className="text-sm font-medium text-zinc-100 flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-yellow-400" />
                  Hide Each-Way Bets
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  When enabled, E/W bet buttons are hidden from the trader panel
                </p>
              </div>
              <button
                onClick={() => updateSetting('hide_ew', !settings.hide_ew)}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.hide_ew ? 'bg-yellow-600' : 'bg-zinc-700'
                }`}
                data-testid="hide-ew-toggle"
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  settings.hide_ew ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            {/* Hide Place Toggle */}
            <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded border border-zinc-800">
              <div>
                <h3 className="text-sm font-medium text-zinc-100 flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-emerald-400" />
                  Hide Place Bets
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  When enabled, Place bet buttons are hidden from the trader panel and place bets cannot be placed
                </p>
              </div>
              <button
                onClick={() => updateSetting('hide_place', !settings.hide_place)}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.hide_place ? 'bg-emerald-600' : 'bg-zinc-700'
                }`}
                data-testid="hide-place-toggle"
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  settings.hide_place ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            {/* Protected Username */}
            <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded border border-zinc-800">
              <div className="flex-1 mr-4">
                <h3 className="text-sm font-medium text-zinc-100 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-400" />
                  Protected Username
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  This user account cannot be deleted by any admin
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={settings.protected_username || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, protected_username: e.target.value }))}
                  placeholder="e.g. User64"
                  className="w-36 h-8 bg-black border-zinc-800 text-zinc-100 text-sm"
                  data-testid="protected-username-input"
                />
                <Button
                  size="sm"
                  className="h-8 bg-blue-600 hover:bg-blue-700"
                  onClick={() => updateSetting('protected_username', settings.protected_username)}
                  data-testid="save-protected-btn"
                >
                  Save
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Export/Import */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="py-4">
            <CardTitle className="text-base font-heading text-zinc-100 flex items-center gap-2">
              <Database className="w-5 h-5 text-emerald-500" />
              Data Migration
            </CardTitle>
            <CardDescription className="text-zinc-500">
              Export and import data for migration to another instance
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Export */}
            <div className="p-4 bg-zinc-800/50 rounded border border-zinc-800 space-y-3">
              <h3 className="text-sm font-medium text-zinc-100 flex items-center gap-2">
                <Download className="w-4 h-4 text-emerald-400" />
                Export Data
              </h3>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="exportMode"
                    value="all"
                    checked={exportMode === 'all'}
                    onChange={() => setExportMode('all')}
                    className="accent-blue-500"
                  />
                  <span className="text-sm text-zinc-300">All data</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="exportMode"
                    value="users_venues"
                    checked={exportMode === 'users_venues'}
                    onChange={() => setExportMode('users_venues')}
                    className="accent-blue-500"
                  />
                  <span className="text-sm text-zinc-300">Users &amp; Venues only</span>
                </label>
              </div>
              <Button
                onClick={handleExport}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export to File
              </Button>
            </div>

            {/* Import */}
            <div className="p-4 bg-zinc-800/50 rounded border border-zinc-800 space-y-3">
              <h3 className="text-sm font-medium text-zinc-100 flex items-center gap-2">
                <Upload className="w-4 h-4 text-blue-400" />
                Import Data
              </h3>
              <p className="text-xs text-zinc-500">
                Upload a previously exported JSON file to restore data
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
                data-testid="import-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                variant="outline"
                className="border-blue-800 text-blue-400 hover:bg-blue-900/20"
                data-testid="import-btn"
              >
                {importing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />Import from File</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="bg-zinc-900 border-red-900/50">
          <CardHeader className="py-4">
            <CardTitle className="text-base font-heading text-red-400 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-red-950/20 border border-red-900/30 rounded">
              <div>
                <h3 className="text-sm font-medium text-zinc-100 flex items-center gap-2">
                  <Database className="w-4 h-4 text-red-400" />
                  Clear All Data
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Delete all bets, runners, and races. User accounts will be preserved.
                </p>
              </div>
              <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline"
                    className="border-red-800 text-red-400 hover:bg-red-900/20"
                    data-testid="clear-data-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear Data
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                  <DialogHeader>
                    <DialogTitle className="text-zinc-100 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      Clear All Data
                    </DialogTitle>
                  </DialogHeader>
                  
                  {clearResult ? (
                    <div className="py-4 space-y-4">
                      <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded">
                        <h3 className="text-sm font-medium text-emerald-400 mb-2">Data Cleared Successfully</h3>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-mono font-bold text-zinc-100">{clearResult.bets_deleted}</p>
                            <p className="text-xs text-zinc-500">Bets</p>
                          </div>
                          <div>
                            <p className="text-2xl font-mono font-bold text-zinc-100">{clearResult.runners_deleted}</p>
                            <p className="text-xs text-zinc-500">Runners</p>
                          </div>
                          <div>
                            <p className="text-2xl font-mono font-bold text-zinc-100">{clearResult.races_deleted}</p>
                            <p className="text-xs text-zinc-500">Races</p>
                          </div>
                        </div>
                      </div>
                      <Button 
                        className="w-full" variant="outline"
                        onClick={() => { setShowClearDialog(false); setClearResult(null); }}
                      >
                        Close
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="py-4 space-y-4">
                        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded">
                          <p className="text-sm text-red-400"><strong>Warning:</strong> This will permanently delete all bets, runners, and races.</p>
                          <p className="text-sm text-zinc-500 mt-2">User accounts and balances will NOT be affected.</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Type DELETE ALL to confirm</Label>
                          <Input
                            placeholder="DELETE ALL"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            className="bg-black border-zinc-800 text-zinc-100 font-mono"
                            data-testid="confirm-delete-input"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline" className="border-zinc-700 text-zinc-400">Cancel</Button>
                        </DialogClose>
                        <Button 
                          onClick={handleClearData}
                          disabled={clearing || confirmText !== 'DELETE ALL'}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid="confirm-clear-btn"
                        >
                          {clearing ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Clearing...</>
                          ) : (
                            <><Trash2 className="w-4 h-4 mr-2" />Clear All Data</>
                          )}
                        </Button>
                      </DialogFooter>
                    </>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
